// CajaSummary.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { formatCurrency } from '../../../../../../../../../../utils/functions';
import { CajaMovementSessionSummary } from '../../../../types';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';
import { FaTrash, FaProjectDiagram } from 'react-icons/fa';
import './CajaSummary.scss';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

interface CajaSummaryProps {
  cajaSummary: CajaMovementSessionSummary | null;
  cajasStatus: any[];
  handleReportedAmountChange: any;
  userReportedData: any;
  setCajaDifference: any;
  onFinalCajaMovementsChange: (finalMovements: any) => void;
  showBalances: boolean;
  missingInputs?: string[];
  tipSummary?: any;
}

const CajaSummary: React.FC<CajaSummaryProps> = ({
  cajaSummary,
  cajasStatus,
  handleReportedAmountChange,
  userReportedData,
  setCajaDifference,
  onFinalCajaMovementsChange,
  showBalances,
  missingInputs = [],
  tipSummary,
}) => {
  const [selectedCajaDetails, setSelectedCajaDetails] = useState<any>(null);
  const [branchedTags, setBranchedTags] = useState<{ [key: string]: { tag: string; amount: number }[] }>({});

  // Validate and sanitize numeric input
  const handleNumericInput = (value: string): number | '' => {
    if (value === '' || value === '-') return '';
    // Allow only numbers, one decimal point, and optional negative sign
    const sanitized = value.replace(/[^0-9.-]/g, '');
    // Prevent multiple decimal points
    const parts = sanitized.split('.');
    const cleaned = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitized;
    // Prevent multiple negative signs and ensure it's only at the start
    const final = cleaned.replace(/(?!^)-/g, '').replace(/^-+/, '-');
    const num = parseFloat(final);
    return isNaN(num) ? '' : num;
  };

  const calculateDifference = (actual: number, reported: number) => reported - actual;

  const handleAddTag = (workstationId: string) => {
    const updatedTags = branchedTags[workstationId] || [];
    setBranchedTags({
      ...branchedTags,
      [workstationId]: [...updatedTags, { tag: '', amount: 0 }],
    });
  };

  const handleRemoveTag = (workstationId: string, index: number) => {
    // Clonamos el array y eliminamos el tag en la posición dada
    const updatedTags = [...(branchedTags[workstationId] || [])];
    updatedTags.splice(index, 1);

    if (updatedTags.length === 0) {
      // Si ya no quedan tags, borramos la entrada y mandamos 0
      const newBranched = { ...branchedTags };
      delete newBranched[workstationId];
      setBranchedTags(newBranched);
      handleReportedAmountChange(`caja_${workstationId}`, 0);
    } else {
      // Si aún hay tags, actualizamos el estado...
      setBranchedTags({ ...branchedTags, [workstationId]: updatedTags });
      // ...y recalculamos la suma de montos para notificar al padre
      const newSum = updatedTags.reduce((sum, t) => sum + (t.amount || 0), 0);
      handleReportedAmountChange(`caja_${workstationId}`, newSum);
    }
  };


  const handleTagChange = (workstationId: string, index: number, key: 'tag' | 'amount', value: string | number) => {
    const updatedTags = [...(branchedTags[workstationId] || [])];
    // Map 'amount' to the actual property name
    if (key === 'amount') {
      updatedTags[index] = { ...updatedTags[index], amount: value as number };
    } else {
      updatedTags[index] = { ...updatedTags[index], tag: value as string };
    }
    setBranchedTags({ ...branchedTags, [workstationId]: updatedTags });
    const reportedAmount = updatedTags.reduce((acc, tag) => acc + (tag.amount || 0), 0);
    handleReportedAmountChange(`caja_${workstationId}`, reportedAmount);
  };

  const toggleCajaDetails = (caja: any) => {
    if (selectedCajaDetails?.name === caja.name) {
      setSelectedCajaDetails(null);
    } else {
      setSelectedCajaDetails(caja);
    }
  };

  // Check if a caja is closed (has been previously reconciled)
  const isCajaClosed = useCallback((caja: any, workstationId: string) => {
    // Find the caja status from the cajasStatus array using workstation_id
    const cajaStatus = cajasStatus.find(status => status.workstation_id.toString() === workstationId);
    
    // Return true if the caja is closed according to the status API
    return cajaStatus ? cajaStatus.is_open === false : false;
  }, [cajasStatus]);

  // Auto-set reported amount for closed cajas and expand first caja details by default
  useEffect(() => {
    if (cajaSummary) {
      // Set first caja as expanded by default
      const firstCaja = Object.values(cajaSummary.cajas)[0];
      if (firstCaja && !selectedCajaDetails) {
        setSelectedCajaDetails(firstCaja);
      }

      Object.entries(cajaSummary.cajas).forEach(([workstationId, caja]: [string, any]) => {
        const isClosedCaja = isCajaClosed(caja, workstationId);
        const currentReported = userReportedData[`caja_${workstationId}`];
        
        if (isClosedCaja && (currentReported === undefined || currentReported === null || currentReported === '')) {
          // Auto-set the final amount for closed cajas
          handleReportedAmountChange(`caja_${workstationId}`, caja.current_amount);
        }
      });
    }
  }, [cajaSummary, cajasStatus, userReportedData, handleReportedAmountChange, isCajaClosed]);

  // Report final caja movements including tags
  useEffect(() => {
    if (cajaSummary) {
      const finalMovements = Object.entries(cajaSummary.cajas).reduce((acc, [workstationId, data]) => {
        const reported = userReportedData[`caja_${workstationId}`] || 0;
        const tags = branchedTags[workstationId] || [];
        acc[workstationId] = {
          name: data.name,
          system: data.current_amount,
          reported,
          tags,
        };
        return acc;
      }, {} as any);
      onFinalCajaMovementsChange(finalMovements);
    }
  }, [cajaSummary, branchedTags, userReportedData, onFinalCajaMovementsChange]);

  // Update caja difference
  useEffect(() => {
    if (cajaSummary) {
      const totalDifference = Object.entries(cajaSummary.cajas).reduce((acc, [workstationId, caja]: [string, any]) => {
        const reported = userReportedData[`caja_${workstationId}`] || 0;
        const difference = calculateDifference(caja.current_amount, reported);
        return acc + (Math.abs(difference) < caja.current_amount * 0.0001 ? 0 : difference);
      }, 0);
      setCajaDifference(totalDifference);
    }
  }, [cajaSummary, userReportedData, branchedTags, setCajaDifference]);

  return (
    <div className="caja-summary-section">
      <div className='caja-summary-section-container-title-toolstips'>
        <h5>Detalle Cajas (Efectivo)</h5>
      </div>

      <div className="caja-methods-container">
        {cajaSummary &&
          Object.entries(cajaSummary.cajas).map(([workstationId, caja]: [string, any]) => {
            const reported = userReportedData[`caja_${workstationId}`] || '';
            const difference = calculateDifference(caja.current_amount, reported);
            const EPS = 0.01;
            const displayDiff = Math.abs(difference) < EPS ? 0 : difference;
            const isClosedCaja = isCajaClosed(caja, workstationId);
            
            return (
              <div key={workstationId} className={`caja-method-card ${isClosedCaja ? 'closed-caja' : ''}`}>
                <div className="caja-method-content">
                  <div className="caja-type-column">
                    <Button
                      variant="link"
                      className="toggle-details-button"
                      onClick={() => toggleCajaDetails(caja)}
                    >
                      {selectedCajaDetails?.name === caja.name ? <ChevronUp /> : <ChevronDown />}
                    </Button>
                    <div className="caja-type">
                      {caja.name}
                      <span className={`status-indicator ${isClosedCaja ? 'closed' : 'open'}`}>
                        {isClosedCaja ? 'CERRADA' : 'ABIERTA'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="system-amount">
                    <div className="amount-label">Sistema</div>
                    <div className="amount-value">
                      {!showBalances ? (
                        <span className="blur-balance">***</span>
                      ) : (
                        formatCurrency(caja.current_amount)
                      )}
                    </div>
                  </div>
                  
                  <div className="input-section">
                    <InputGroup className="amount-input-group">
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="text"
                        className={`amount-input ${missingInputs.includes(`caja_${workstationId}`) ? 'input-missing' : ''}`}
                        placeholder="Monto"
                        value={userReportedData[`caja_${workstationId}`] ?? ''}
                        onChange={(e) => {
                          const validatedValue = handleNumericInput(e.target.value);
                          handleReportedAmountChange(`caja_${workstationId}`, validatedValue);
                        }}
                        disabled={branchedTags[workstationId]?.length > 0 || isClosedCaja}
                        readOnly={isClosedCaja}
                      />
                    </InputGroup>
                    {showBalances && !isClosedCaja && (
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Igualar al sistema</Tooltip>}
                      >
                        <Button
                          variant="none"
                          className="mp-button light equal-button"
                          onClick={() => handleReportedAmountChange(`caja_${workstationId}`, caja.current_amount)}
                          disabled={branchedTags[workstationId]?.length > 0}
                          title="Igualar al sistema"
                        >
                          =
                        </Button>
                      </OverlayTrigger>
                    )}
                    {isClosedCaja && (
                      <div className="closed-label">
                        <span>CERRADA</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="difference-section">
                    <div className="difference-label">Diferencia</div>
                    <div className={`difference-value ${!showBalances ? '' : (Math.abs(displayDiff) < EPS ? 'difference-green' : (displayDiff > 0 ? 'difference-green' : 'difference-red'))}`}>
                      {!showBalances ? (
                        <span className="blur-balance">***</span>
                      ) : (
                        formatCurrency(displayDiff)
                      )}
                    </div>
                  </div>
                  
                  <div className="caja-actions">
                    {!isClosedCaja && (
                      <Button
                        variant="none"
                        className="mp-button light"
                        onClick={() => handleAddTag(workstationId)}
                        title="Agregar detalle"
                      >
                        <FaProjectDiagram />
                      </Button>
                    )}
                  </div>
                </div>

                {branchedTags[workstationId] && branchedTags[workstationId].length > 0 && (
                  <div className="tag-section" onClick={(e) => e.stopPropagation()}>
                    {branchedTags[workstationId].map((tag, index) => (
                      <div key={`${workstationId}_tag_${index}`} className="tag-item">
                        <Form.Control
                          type="text"
                          className="tag-input"
                          placeholder="Etiqueta"
                          value={tag.tag}
                          onChange={(e) => handleTagChange(workstationId, index, 'tag', e.target.value)}
                        />
                        <div className="tag-amount-input">
                          <InputGroup className="amount-input-group">
                            <InputGroup.Text>$</InputGroup.Text>
                            <Form.Control
                              type="text"
                              placeholder="Monto"
                              className="amount-input"
                              value={tag.amount}
                              onChange={(e) => {
                                const validatedValue = handleNumericInput(e.target.value);
                                handleTagChange(workstationId, index, 'amount', validatedValue);
                              }}
                            />
                          </InputGroup>
                        </div>
                        <button
                          className="tag-remove-btn"
                          onClick={() => handleRemoveTag(workstationId, index)}
                          title="Eliminar etiqueta"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {selectedCajaDetails?.name === caja.name && (
                  <div className="details-section">
                    <div className="detail-row">
                      <span className="detail-label">Balance Inicial:</span>
                      <span className="detail-value">
                        {!showBalances ? (
                          <span className="blur-balance">***</span>
                        ) : (
                          formatCurrency(caja.opening_balance)
                        )}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Total Movimientos:</span>
                      <span className="detail-value">
                        {!showBalances ? (
                          <span className="blur-balance">***</span>
                        ) : (
                          formatCurrency(caja.movement_balance)
                        )}
                      </span>
                    </div>
                    {tipSummary?.tips && (
                      <div className="detail-row">
                        <span className="detail-label">Total Propinas:</span>
                        <span className="detail-value">
                          {!showBalances ? (
                            <span className="blur-balance">***</span>
                          ) : (
                            formatCurrency(
                              Object.entries(tipSummary.tips).reduce((sum: number, [key, tip]: [string, any]) => {
                                // Only include cash tips (Propina_Efectivo), exclude card and transfer tips
                                if (key === 'Propina_Efectivo') {
                                  return sum + (tip.amount || 0);
                                  
                                }
                                return sum;
                              }, 0)
                            )
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default CajaSummary;
